import React, { useMemo, useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CircadiaBarChart from './CircadiaBarChart';
import type { GraphDistribution } from '../GraphBox';

interface FullWidthBarChartProps {
	title: string;
	data: GraphDistribution[];
	xaxislabel: string;
	yaxislabel: string;
	registerExportFn?: (exportFn: () => void) => void;
}

const getMidViewWidth = (viewportWidth: number) => {
	if (viewportWidth >= 950 && viewportWidth < 1000) {
		return { width: viewportWidth - 150, height: 260 };
	}
	if (viewportWidth >= 900 && viewportWidth < 950) {
		return { width: viewportWidth - 200, height: 260 };
	}
	if (viewportWidth >= 750 && viewportWidth < 900) {
		return { width: viewportWidth - 300, height: 260 };
	}
	if (viewportWidth >= 700 && viewportWidth < 750) {
		return { width: viewportWidth - 325, height: 260 };
	}
	if (viewportWidth >= 600 && viewportWidth < 700) {
		return { width: viewportWidth - 250, height: 260 };
	}
	// For larger medium screens
	return { width: 800, height: 260 };
};

const getMobileViewWidth = (viewportWidth: number) => {
	if (viewportWidth < 500) {
		return { width: viewportWidth - 100, height: 200 };
	}
	return { width: 450, height: 200 };
};

/**
 * CircadiaFullWidthBarChart - A wrapper around CircadiaBarChart that applies full-width sizing
 * All props are passed through to CircadiaBarChart
 */
const CircadiaFullWidthBarChart: React.FC<FullWidthBarChartProps> = (props) => {
	const theme = useTheme();

	// Track viewport size with state
	const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isMd = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
	const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

	useEffect(() => {
		const handleResize = () => {
			setViewportWidth(window.innerWidth);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const chartSize = useMemo(() => {
		if (isMobile) {
			return getMobileViewWidth(viewportWidth);
		}
		if (isMd) {
			return getMidViewWidth(viewportWidth);
		}
		if (isLgUp) return { width: 1100, height: 300 };
		return { width: 550, height: 300 };
	}, [viewportWidth, isMobile, isMd, isLgUp]);

    return (
        <CircadiaBarChart
            {...props}
            chartSize={chartSize}
            fullWidth={true}
        />
    );
};

export default CircadiaFullWidthBarChart;
